import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import defaultSettings from '../config/defaultSettings';
import '@ant-design/v5-patch-for-react-19';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { getMe } from '@/services/api/user';
import { amplifyCustom } from '@/utils/amplify';
import outputs from '../amplify_outputs.json';

Amplify.configure(outputs);

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;

// ✅ Amplify UI のログインページ（Authenticator）を置くパス
// 例: src/pages/Login/index.tsx を作って /login にルーティングする
const loginPath = '/login';

// ✅ ログイン不要のパス
const publicPaths = [loginPath, '/auth/callback'];

// redirect 付きでログインへ飛ばす
function redirectToLogin() {
  const { location } = history;
  const redirect = encodeURIComponent(
    location.pathname + (location.search || ''),
  );
  history.replace(`${loginPath}?redirect=${redirect}`);
}

async function fetchAmplifyUser() {
  try {
    const user = await getCurrentUser();
    return user;
  } catch {
    return null;
  }
}

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: any;
  loading?: boolean;
  fetchUserInfo?: () => Promise<any | undefined>;
}> {
  const fetchUserInfo = async () => {
    // 1) Cognitoでログイン確認
    const user = await fetchAmplifyUser();
    if (!user) return undefined;

    // 2) DynamoDBのプロフィール取得（/me）
    try {
      const profile = await getMe();

      // 3) currentUser は DynamoDB を正にする
      //    必要なら Cognito 情報も少しだけ合成
      return {
        ...profile,
        // 例：Cognito の sub を残したい場合だけ
        cognito: {
          username: user.username,
          userId: user.userId,
        },
      };
    } catch (e) {
      // プロフィールが無い / 権限エラーなど
      console.error(e);
      return undefined;
    }
  };

  const { location } = history;

  if (publicPaths.includes(location.pathname)) {
    return {
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  const currentUser = await fetchUserInfo();

  if (!currentUser) {
    redirectToLogin();
    return {
      fetchUserInfo,
      currentUser: undefined,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [],
    avatarProps: {
      src: undefined,
      title: <AvatarName />,
      render: () => {
        return (
          <AvatarDropdown>
            <AvatarName />
          </AvatarDropdown>
        );
      },
    },
    footerRender: () => <Footer />,
    onPageChange: async () => {
      const { location } = history;

      // public ページならガードしない
      if (publicPaths.includes(location.pathname)) return;

      // initialState に currentUser があっても、リロード直後などでズレることがあるので都度チェック
      const user = await fetchAmplifyUser();
      if (!user) {
        redirectToLogin();
        return;
      }

      // initialState が空なら埋める（任意）
      if (!initialState?.currentUser) {
        setInitialState((s) => ({ ...s, currentUser: user }));
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev ? [] : [],
    menuHeaderRender: undefined,
    childrenRender: (children) => {
      return (
        <>
          {children}
          {false && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

export const request: RequestConfig = {
  // ✅ amplify outputs から baseURL を設定
  baseURL: amplifyCustom.apiBaseUrl,

  timeout: 30000,

  // ✅ 全リクエストに Cognito ID Token を自動付与
  requestInterceptors: [
    async (config) => {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (idToken) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${idToken}`,
        };
      }

      return config;
    },
  ],

  // （任意）共通エラーハンドリング
  errorConfig: {
    errorThrower: (res) => {
      const { success, message } = res as any;
      if (success === false) {
        throw new Error(message || 'API Error');
      }
    },
  },
};
